import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper,
} from '@mui/material'
import { Calculate, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'

const DEMO_DATA = [
  { item: '10001', description: 'HV CHILI BNS 15oz', packSize: '24/15oz', casesPerEC: 1.00, ecFactor: 1.00 },
  { item: '10002', description: 'FINEST CHILI BNS 15oz', packSize: '24/15oz', casesPerEC: 1.00, ecFactor: 1.00 },
  { item: '10005', description: 'HV CHILI NO BNS 15oz', packSize: '24/15oz', casesPerEC: 1.00, ecFactor: 1.00 },
  { item: '10010', description: 'HV CHICKEN NOODLE 15oz', packSize: '24/15oz', casesPerEC: 1.18, ecFactor: 0.85 },
  { item: '10011', description: 'HV TOMATO SOUP 10.75oz', packSize: '24/10.75oz', casesPerEC: 1.39, ecFactor: 0.72 },
  { item: '10012', description: 'HV CREAM OF MUSHROOM 10.75oz', packSize: '24/10.75oz', casesPerEC: 1.39, ecFactor: 0.72 },
  { item: '10020', description: 'HV PORK & BEANS 15oz', packSize: '24/15oz', casesPerEC: 1.00, ecFactor: 1.00 },
  { item: '10021', description: 'HV BAKED BEANS 28oz', packSize: '12/28oz', casesPerEC: 0.53, ecFactor: 1.87 },
  { item: '10030', description: 'GMI PROGRESSO CHKN 19oz', packSize: '12/19oz', casesPerEC: 0.79, ecFactor: 1.27 },
  { item: '10031', description: 'GMI PROGRESSO MINESTRONE 19oz', packSize: '12/19oz', casesPerEC: 0.79, ecFactor: 1.27 },
]

export default function ECConversion() {
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('item')
  const [order, setOrder] = useState('asc')

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    let filtered = DEMO_DATA.filter((r) =>
      `${r.item} ${r.description} ${r.packSize}`.toLowerCase().includes(search.toLowerCase()),
    )
    filtered.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [search, orderBy, order])

  const columns = [
    { id: 'item', label: 'Item #' },
    { id: 'description', label: 'Description' },
    { id: 'packSize', label: 'Pack Size' },
    { id: 'casesPerEC', label: 'Cases per EC', align: 'right' },
    { id: 'ecFactor', label: 'EC Factor', align: 'right' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Calculate color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>EC Conversion</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="EC Factor Table" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v6 · {DEMO_DATA.length} items
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Search items…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.item} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.item}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.packSize}</TableCell>
                <TableCell align="right">{row.casesPerEC.toFixed(2)}</TableCell>
                <TableCell align="right">{row.ecFactor.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
